import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, ShieldCheck, Network, Layers, ArrowRight, CheckCircle2 } from 'lucide-react';
import { TiltCard } from './TiltCard';
/**
 * Vier dingen die geen quiz-app heeft.
 * Editorial asymmetric grid (7-5-5-7 cols).
 */
const PILLARS = [{
  n: '01',
  icon: GraduationCap,
  tag: 'Lesplan',
  title: 'Op het echte Nederlandse lesplan',
  desc: 'Geen losse spelletjes. Elke vraag hangt aan een leerdoel uit het officiële lesplan voor groep 6, 7 en 8 — automatisch afgestemd op het Begin, Midden of Eind van het schooljaar.',
  accent: 'from-emerald-500 to-teal-500',
  bg: 'bg-emerald-50',
  border: 'border-emerald-200',
  text: 'text-emerald-700',
  chips: ['10 vakken', '100+ leerdoelen', '16 niveaus']
}, {
  n: '02',
  icon: ShieldCheck,
  tag: 'Kwaliteit',
  title: 'Wij keuren elke vraag 8 keer',
  desc: 'Voordat een vraag bij je kind belandt, doorloopt hij 8 kwaliteitschecks: van vorm en inhoud tot uitleg en moeilijkheid. Niets glipt erdoorheen — ook geen "bijna goed".',
  accent: 'from-orange-500 to-rose-500',
  bg: 'bg-orange-50',
  border: 'border-orange-200',
  text: 'text-orange-700',
  chips: ['Vorm', 'Vraagtype', 'Leerkaart', 'Inhoud', 'Niveau', 'Hulpmiddel', 'Uitleg', 'Eindkeuring']
}, {
  n: '03',
  icon: Network,
  tag: 'Routekaart',
  title: 'djay weet wélke stap je kind nu nodig heeft',
  desc: 'Elke vaardigheid hangt aan voorkennis. Tafels eerst, dan schatten, dan procenten. De routekaart van leerdoelen zorgt dat je kind nooit een stap overslaat — en nooit vastloopt op iets waar het nog niet klaar voor is.',
  accent: 'from-violet-500 to-fuchsia-500',
  bg: 'bg-violet-50',
  border: 'border-violet-200',
  text: 'text-violet-700',
  chips: ['Eerst de basis', 'Auto-ontgrendelen', 'Herhalen op het juiste moment']
}, {
  n: '04',
  icon: Layers,
  tag: 'Variatie',
  title: '38 vraagtypes — elk concept een eigen vorm',
  desc: 'Multiple choice is maar het begin. Getallenlijnen, klokken, breukentaarten, kaarten, zinsontleding, drag & drop, sorteren, tekenen. Elk concept krijgt het type dat erbij past — met 55 visuele hulpmiddelen erbij. Op weg naar 200+ in 2025.',
  accent: 'from-sky-500 to-indigo-500',
  bg: 'bg-sky-50',
  border: 'border-sky-200',
  text: 'text-sky-700',
  chips: ['38 vraagtypes', '55 hulpmiddelen', 'richting 200+']
}];
export function DifferentiationSection() {
  return <section id="anders" className="relative py-24 lg:py-32 bg-white overflow-hidden" data-id="element-93">
      <div className="absolute inset-0 pointer-events-none" data-id="element-94">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-200/25 rounded-full blur-3xl" data-id="element-95" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-200/20 rounded-full blur-3xl" data-id="element-96" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-97">
        {/* Section header */}
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-8 mb-16 items-end" data-id="element-98">
          <div data-id="element-99">
            <motion.div initial={{
            opacity: 0,
            y: 10,
            rotate: -3
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotate: -2
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6 shadow-lg shadow-slate-900/20" data-id="element-100">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" data-id="element-101" />
              Wat ons écht anders maakt
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[0.95]" data-id="element-102">
              Vier dingen die{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-103">
                geen
              </span>{' '}
              quiz-app heeft.
            </h2>
          </div>
          <p className="text-lg text-slate-600 font-medium md:pb-2" data-id="element-104">
            De meeste oefen-apps tonen vragen en houden score bij. Wij bouwen
            onder de motorkap een leraar die kwaliteit, volgorde én niveau
            bewaakt.
          </p>
        </div>

        {/* Asymmetric grid */}
        <div className="grid md:grid-cols-12 gap-6" data-id="element-105">
          {PILLARS.map((p, idx) => {
          const Icon = p.icon;
          const span = idx === 0 ? 'md:col-span-7' : idx === 1 ? 'md:col-span-5' : idx === 2 ? 'md:col-span-5' : 'md:col-span-7';
          return <motion.div key={p.n} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: idx * 0.1,
            duration: 0.5
          }} className={span} data-id="element-106">
                <TiltCard intensity={5} className="group relative bg-white rounded-3xl p-8 lg:p-10 border-2 border-slate-100 shadow-sm hover:shadow-2xl transition-shadow overflow-hidden h-full" data-id="element-107">
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${p.accent}`} data-id="element-108" />

                  {/* Big background number */}
                  <div aria-hidden="true" className="absolute -top-6 -right-4 text-[12rem] font-black text-slate-100 leading-none pointer-events-none select-none" data-id="element-109">
                    {p.n}
                  </div>

                  <div className="relative flex items-center gap-3 mb-5" style={{
                transform: 'translateZ(20px)'
              }} data-id="element-110">
                    <motion.div className={`w-12 h-12 rounded-2xl ${p.bg} border ${p.border} flex items-center justify-center shrink-0`} whileHover={{
                  rotate: [-3, 3, 0],
                  scale: 1.08
                }} transition={{
                  duration: 0.4
                }} data-id="element-111">
                      <Icon className={`w-6 h-6 ${p.text}`} strokeWidth={2.5} data-id="element-112" />
                    </motion.div>
                    <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${p.text}`} data-id="element-113">
                      {p.tag}
                    </div>
                  </div>

                  <h3 className="relative text-2xl lg:text-3xl font-black text-slate-900 leading-[1.1] mb-4" style={{
                transform: 'translateZ(15px)'
              }} data-id="element-114">
                    {p.title}
                  </h3>

                  <p className="relative text-slate-600 leading-relaxed font-medium mb-6 max-w-prose" style={{
                transform: 'translateZ(10px)'
              }} data-id="element-115">
                    {p.desc}
                  </p>

                  <div className="relative flex flex-wrap gap-2" style={{
                transform: 'translateZ(15px)'
              }} data-id="element-116">
                    {p.chips.map(chip => <div key={chip} className={`inline-flex items-center gap-1 ${p.bg} border ${p.border} rounded-full px-2.5 py-1`} data-id="element-117">
                        <CheckCircle2 className={`w-3 h-3 ${p.text} shrink-0`} strokeWidth={3} data-id="element-118" />
                        <span className={`text-[10px] font-black ${p.text}`} data-id="element-119">
                          {chip}
                        </span>
                      </div>)}
                  </div>
                </TiltCard>
              </motion.div>;
        })}
        </div>

        {/* CTA strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 bg-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-2xl shadow-slate-900/20" data-id="element-120">
          <div className="flex items-center gap-4" data-id="element-121">
            <div className="w-12 h-12 rounded-2xl bg-orange-500/20 flex items-center justify-center" data-id="element-122">
              <ShieldCheck className="w-6 h-6 text-orange-400" strokeWidth={2.5} data-id="element-123" />
            </div>
            <div data-id="element-124">
              <div className="text-sm md:text-base font-black leading-tight" data-id="element-125">
                Onder de motorkap: 8 kwaliteitschecks · routekaart van
                leerdoelen · 38 vraagtypes
              </div>
              <div className="text-xs text-slate-400 font-medium mt-1" data-id="element-126">
                Bovenop: een vriendelijke vlam die je kind helpt.
              </div>
            </div>
          </div>
          <a href="#kwaliteit" className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-2xl font-bold px-5 py-3 text-sm shadow-lg hover:bg-orange-100 transition-colors whitespace-nowrap" data-id="element-127">
            Bekijk de checks
            <ArrowRight className="w-4 h-4" data-id="element-128" />
          </a>
        </motion.div>
      </div>
    </section>;
}